/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";

import { getLevelMetadata, getLevelById, getQuests } from "../../../api";

export interface Level {
  id: number;
  name: string;
  description: string;
  status: "locked" | "in_progress" | "passed";
  rankName: string;
}

interface LevelContextType {
  levels: Level[];
  levelsLoading: boolean;
  error: Error | null;
  refetchLevels: () => Promise<void>;
  isLoadingLevelDetails: boolean;
  levelDetails: Record<number, Level[]>;
  fetchQuests: () => Promise<void>;
  refetchQuests: () => Promise<void>;
  quests: Level[];
  isLoadingQuests: boolean;
  questsError: Error | null;
}

const LevelContext = createContext<LevelContextType | undefined>(undefined);

// Sort an array of paths/quests alphabetically by name (case-insensitive, locale-aware).
// Tolerates null/undefined and non-array inputs by returning an empty array
const sortByName = <T extends { name?: string }>(items: T[] | null | undefined): T[] => {
  if (!Array.isArray(items)) return [];
  return [...items].sort((a, b) =>
    (a.name ?? "").localeCompare(b.name ?? "", undefined, { sensitivity: "base" })
  );
};

interface LevelContextProviderProps {
  children: ReactNode;
}

export const LevelContextProvider: React.FC<LevelContextProviderProps> = ({ children }) => {
  const [levels, setLevels] = useState<Level[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingLevelDetails, setIsLoadingLevelDetails] = useState<boolean>(false);
  const [levelDetails, setLevelDetails] = useState<Record<number, Level[]>>({});
  const [error, setError] = useState<Error | null>(null);

  const [quests, setQuests] = useState<Level[]>([]);
  const [questsFetched, setQuestsFetched] = useState(false);
  const [isLoadingQuests, setIsLoadingQuests] = useState<boolean>(false);
  const [questsError, setQuestsError] = useState<Error | null>(null);

  // if the user has levels that are locked, we shouldn't show them or care about them in anyway
  const filterAvailableLevels = (levels: Level[]) => {
    return levels.filter(level => level.status !== "locked");
  };

  const fetchLevels = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const levelData = await getLevelMetadata();
      setLevels(filterAvailableLevels(levelData));
    } catch (err) {
      console.error("Failed to fetch level metadata:", err);
      setError(err instanceof Error ? err : new Error("Failed to fetch level metadata"));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLevels();
  }, []);

  const fetchLevelDetails = async () => {
    if (levels.length === 0) return;

    setIsLoadingLevelDetails(true);

    try {
      // Fetch level metadata for each level they have available
      levels.forEach(level => {
        getLevelById(level.id)
          .then(data => {
            setLevelDetails(prev => ({ ...prev, [level.id]: sortByName<Level>(data) }));
          })
          .catch(err => console.error(`Error fetching level ${level.id}:`, err));
      });
    } catch (error) {
      console.error("Error fetching level data:", error);
      setError(error instanceof Error ? error : new Error("Failed to fetch level details"));
    } finally {
      setIsLoadingLevelDetails(false);
    }
  };

  useEffect(() => {
    if (levels.length > 0 && !isLoading) {
      fetchLevelDetails();
    }
  }, [levels, isLoading]);

  const fetchQuests = async () => {
    setIsLoadingQuests(true);
    try {
      const questData = await getQuests();
      setQuests(sortByName<Level>(questData));
    } catch (err) {
      setQuestsError(err instanceof Error ? err : new Error("Failed to fetch quests"));
    } finally {
      setIsLoadingQuests(false);
      setQuestsFetched(true);
    }
  };

  useEffect(() => {
    if (!questsFetched && !isLoadingQuests) {
      fetchQuests();
    }
  }, [questsFetched, isLoadingQuests]);

  const value: LevelContextType = {
    levels,
    levelsLoading: isLoading,
    error,
    refetchLevels: fetchLevels,
    isLoadingLevelDetails,
    levelDetails,
    fetchQuests,
    refetchQuests: fetchQuests,
    quests,
    isLoadingQuests,
    questsError,
  };

  return <LevelContext.Provider value={value}>{children}</LevelContext.Provider>;
};

export const useLevelContext = (): LevelContextType => {
  const context = useContext(LevelContext);
  if (context === undefined) {
    throw new Error("useLevelContext must be used within a LevelContextProvider");
  }
  return context;
};
